import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import { showToast } from '../components/Toast';
import api from '../utils/api';
import { formatMoney, currencySymbol } from '../utils/money';
import { getGoogleAccounts } from '../utils/googleAccounts';
import KnowledgePanel from '../components/KnowledgePanel';
import McpIntegration from '../components/McpIntegration';
import MetaPixelPanel from '../components/MetaPixelPanel';
import { Box, Title, Tabs, TextInput, Textarea, Button, Modal, Badge, Table, Switch, Loader, Paper, Text, Group, Stack, Alert, Flex, Select, Tooltip, FileButton } from '@mantine/core';
import { CircleNotch, WarningCircle, CheckCircle, UserPlus, Trash, Crown, ShieldCheck, Eye, UploadSimple } from '@phosphor-icons/react';

const TABS = ['profile', 'platforms', 'subscription', 'agent', 'knowledge', 'team', 'agency', 'archive'];

function MetaAccountSwitcher({ onSwitch }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [currentId, setCurrentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(null);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await api.get('/ads/meta/ad-accounts/');
      setAccounts(res.data.accounts || []);
      setCurrentId(res.data.current_account_id || '');
    } catch {}
    setLoading(false);
  };

  const handleSelect = async (acct) => {
    setSwitching(acct.id);
    try {
      await api.post('/ads/meta/ad-accounts/', { account_id: acct.id, account_name: acct.name });
      setCurrentId(acct.id);
      if (onSwitch) onSwitch();
      showToast(t('settings.platforms.metaSwitcher.switchedToast', { name: acct.name }), 'success');
      setOpen(false);
    } catch {}
    setSwitching(null);
  };

  return (
    <>
      <Button variant="outline" color="gray" size="xs" onClick={handleOpen}>{t('settings.platforms.metaSwitcher.changeAccount')}</Button>
      <Modal opened={open} onClose={() => setOpen(false)} title={t('settings.platforms.metaSwitcher.modalTitle')} size="md" centered>
        <Text size="xs" c="dimmed" mb="sm">{t('settings.platforms.metaSwitcher.modalDesc')}</Text>
        {loading ? (
          <Flex align="center" justify="center" py="xl"><Loader size="sm" color="brand" /></Flex>
        ) : accounts.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">{t('settings.platforms.metaSwitcher.noAccounts')}</Text>
        ) : (
          <Stack gap={6} mah={300} style={{ overflowY: 'auto' }}>
            {accounts.map(acct => {
              const isCurrent = acct.id === currentId;
              return (
                <Paper
                  key={acct.id} withBorder p="sm" radius="md"
                  onClick={() => !switching && handleSelect(acct)}
                  style={{
                    cursor: switching ? 'default' : 'pointer',
                    opacity: switching ? 0.5 : 1,
                    borderColor: isCurrent ? 'var(--mantine-color-brand-3)' : undefined,
                    backgroundColor: isCurrent ? 'var(--mantine-color-brand-0)' : undefined,
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Group gap="xs">
                        <Text size="sm" fw={500} truncate>{acct.name || t('settings.common.unnamedAccount')}</Text>
                        {isCurrent && <CheckCircle size={14} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />}
                      </Group>
                      <Group gap="xs" mt={2}>
                        <Text size="xs" ff="monospace" c="dimmed">{acct.id}</Text>
                        {acct.currency && <Text size="xs" c="gray.5">{acct.currency}</Text>}
                        {acct.business && <Text size="xs" c="gray.5">{acct.business}</Text>}
                      </Group>
                    </Box>
                    {switching === acct.id && <Loader size="xs" color="brand" style={{ flexShrink: 0 }} />}
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Modal>
    </>
  );
}

const PLATFORMS = [
  { id: 'google_ads', label: 'Google Ads', icon: 'G', color: 'blue', bgClass: 'bg-blue-50', available: true },
  { id: 'meta_ads', label: 'Meta Ads', desc: 'Facebook + Instagram', icon: 'M', color: 'indigo', bgClass: 'bg-indigo-50', available: true },
  { id: 'microsoft_ads', label: 'Microsoft Ads', desc: 'Bing, MSN, Outlook', icon: 'B', color: 'cyan', bgClass: 'bg-cyan-50', available: false },
  { id: 'tiktok_ads', label: 'TikTok Ads', desc: 'TikTok for Business', icon: 'T', color: 'dark', bgClass: 'bg-gray-100', available: false },
];

function AgencyTab({ org, showSaved }) {
  const { t } = useTranslation();
  const [agencyName, setAgencyName] = useState(org?.agency_name || '');
  const [agencyLogo, setAgencyLogo] = useState(org?.agency_logo || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // window.open() can't send the auth token, so fetch the report with the
  // authenticated client and open it as a blob.
  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.get('/accounts/report/white-label/', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      showToast(t('settings.agency.previewError'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/accounts/organization/', {
        agency_name: agencyName,
        agency_logo: agencyLogo,
      });
      showSaved();
    } catch {
      // silently handle
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post('/accounts/organization/logo/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAgencyLogo(res.data.agency_logo);
      showToast(t('settings.agency.logoSavedToast'));
    } catch (e) {
      showToast(e.response?.data?.error || t('settings.agency.logoUploadError'), 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack gap="md" maw={448}>
      <Paper withBorder p="md" radius="md">
        <Text size="sm" fw={600} mb="sm">{t('settings.agency.planTitle')}</Text>
        <Group justify="space-between">
          <Text size="sm" c="dimmed">{t('settings.agency.campaignLimit')}</Text>
          <Text size="sm" fw={600}>{org?.agency_max_campaigns || 10}</Text>
        </Group>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Text size="sm" fw={600} mb="xs">{t('settings.agency.whiteLabelTitle')}</Text>
        <Text size="xs" c="dimmed" mb="md">
          {t('settings.agency.whiteLabelDesc')}
        </Text>
        <TextInput
          label={t('settings.agency.agencyNameLabel')}
          value={agencyName}
          onChange={(e) => setAgencyName(e.target.value)}
          placeholder={t('settings.agency.agencyNamePlaceholder')}
          description={t('settings.agency.agencyNameDesc')}
          size="sm"
          mb="md"
          styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
        />

        <Text size="11px" fw={500} tt="uppercase" c="gray.6" style={{ letterSpacing: '0.05em' }} mb={6}>{t('settings.agency.logoLabel')}</Text>
        {agencyLogo && (
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 8, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8, background: '#fff', marginBottom: 8 }}>
            <img src={agencyLogo} alt={t('settings.agency.logoAlt')} style={{ maxHeight: 44, maxWidth: 180, objectFit: 'contain' }} />
          </div>
        )}
        <Group gap="sm" mb="xs">
          <FileButton onChange={handleUpload} accept="image/png,image/jpeg,image/webp,image/svg+xml">
            {(props) => (
              <Button {...props} variant="light" color="brand" size="xs" loading={uploading} leftSection={<UploadSimple size={14} />}>
                {agencyLogo ? t('settings.agency.changeLogo') : t('settings.agency.uploadLogo')}
              </Button>
            )}
          </FileButton>
          {agencyLogo && (
            <Button variant="subtle" color="red" size="xs" onClick={() => setAgencyLogo('')}>{t('settings.agency.removeLogo')}</Button>
          )}
        </Group>
        <TextInput
          label={t('settings.agency.logoUrlLabel')}
          value={agencyLogo}
          onChange={(e) => setAgencyLogo(e.target.value)}
          placeholder={t('settings.agency.logoUrlPlaceholder')}
          description={t('settings.agency.logoUrlDesc')}
          size="sm"
          mt="sm"
          mb="md"
          styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
        />

        <Group gap="sm">
          <Button color="brand" size="sm" onClick={handleSave} loading={saving}>
            {t('settings.agency.save')}
          </Button>
          <Button variant="light" color="brand" size="sm" onClick={handlePreview} loading={previewing}>
            {t('settings.agency.previewReport')}
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
}

function GA4Settings({ org, showSaved }) {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ga4Error, setGa4Error] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(org?.ga4_property_id || '');
  const [leadEvent, setLeadEvent] = useState(org?.ga4_lead_event || '');
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const isConnected = !!org?.ga4_property_id;

  const loadEvents = async (propertyId) => {
    if (!propertyId) { setEvents([]); return; }
    setEventsLoading(true);
    try {
      const res = await api.get('/ads/ga4/events/', { params: { property_id: propertyId } });
      setEvents(res.data.status === 'success' ? (res.data.events || []) : []);
    } catch { setEvents([]); } finally { setEventsLoading(false); }
  };

  useEffect(() => {
    if (org?.ga4_property_id) loadEvents(org.ga4_property_id);
  }, [org?.ga4_property_id]);

  const saveLeadEvent = async (value) => {
    setLeadEvent(value || '');
    setSaving(true);
    try {
      await api.post('/ads/ga4/connect/', { property_id: selectedProperty || org.ga4_property_id, lead_event: value || '' });
      showSaved();
    } catch {} finally { setSaving(false); }
  };

  const loadProperties = async () => {
    setLoading(true);
    setGa4Error('');
    try {
      const res = await api.get('/ads/ga4/properties/');
      if (res.data.status === 'success') {
        setProperties(res.data.properties || []);
        if ((res.data.properties || []).length === 0) {
          setGa4Error(t('settings.platforms.ga4.noProperties'));
        }
      } else {
        const msg = res.data.message || '';
        if (msg.includes('SERVICE_DISABLED') || msg.includes('has not been used')) {
          setGa4Error(t('settings.platforms.ga4.errorServiceDisabled'));
        } else if (msg.includes('invalid_grant') || msg.includes('expired')) {
          setGa4Error(t('settings.platforms.ga4.errorTokenExpired'));
        } else {
          setGa4Error(msg || t('settings.platforms.ga4.errorLoadFailed'));
        }
      }
    } catch {
      setGa4Error(t('settings.platforms.ga4.errorConnectFailed'));
    } finally { setLoading(false); }
  };

  const handleConnect = async () => {
    setSaving(true);
    try {
      await api.post('/ads/ga4/connect/', { property_id: selectedProperty, lead_event: leadEvent });
      showSaved();
    } catch {} finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      await api.post('/ads/ga4/connect/', { property_id: '' });
      setSelectedProperty('');
      setLeadEvent('');
      setEvents([]);
      showSaved();
    } catch {} finally { setSaving(false); }
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Text size="sm" fw={600} mb="sm">{t('settings.platforms.ga4.title')}</Text>
      {isConnected ? (
        <Stack gap="xs">
          <Group gap="xs">
            <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-green-5)' }} />
            <Text size="sm" fw={500}>{t('settings.platforms.ga4.connected')}</Text>
          </Group>
          <Text size="xs" c="dimmed">{t('settings.platforms.ga4.propertyIdLabel')} <Text span ff="monospace" c="dark">{org.ga4_property_id}</Text></Text>
          <Select
            label={t('settings.platforms.ga4.leadEventLabel')}
            description={t('settings.platforms.ga4.leadEventHint')}
            placeholder={eventsLoading ? t('settings.platforms.ga4.loading') : t('settings.platforms.ga4.leadEventPlaceholder')}
            nothingFoundMessage={t('settings.platforms.ga4.leadEventNone')}
            data={Array.from(new Set([...(leadEvent ? [leadEvent] : []), ...events]))}
            value={leadEvent || null}
            onChange={saveLeadEvent}
            disabled={saving || eventsLoading}
            clearable
            searchable
            size="sm"
            styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
          />
          <Button variant="outline" color="red" size="xs" onClick={handleDisconnect} loading={saving}>
            {t('settings.platforms.ga4.disconnect')}
          </Button>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            {t('settings.platforms.ga4.intro')}
          </Text>
          {ga4Error && (
            <Alert color="red" variant="light" radius="md">
              <Text size="xs">{ga4Error}</Text>
            </Alert>
          )}
          {properties.length === 0 ? (
            <Button variant="light" color="brand" size="xs" onClick={loadProperties} loading={loading}>
              {loading ? t('settings.platforms.ga4.loading') : t('settings.platforms.ga4.loadList')}
            </Button>
          ) : (
            <>
              <Select
                label={t('settings.platforms.ga4.selectLabel')}
                placeholder={t('settings.platforms.ga4.selectPlaceholder')}
                data={properties.map(p => ({ value: p.property_id, label: `${p.display_name} (${p.property_id})` }))}
                value={selectedProperty}
                onChange={setSelectedProperty}
                size="sm"
                styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
              />
              <Button color="brand" size="xs" onClick={handleConnect} loading={saving} disabled={!selectedProperty}>
                {t('settings.platforms.ga4.connect')}
              </Button>
            </>
          )}
        </Stack>
      )}
    </Paper>
  );
}

// Search Console ukryty do czasu weryfikacji zakresu webmasters.readonly przez Google.
// Włączenie: ustaw na true (backend i przepływ OAuth są gotowe).
const SHOW_GSC = false;

function GSCSettings({ org, showSaved, onChange }) {
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [needsReauth, setNeedsReauth] = useState(false);
  const [selectedSite, setSelectedSite] = useState(org?.gsc_site_url || '');
  const isConnected = !!org?.gsc_connected;

  const loadSites = async () => {
    setLoading(true);
    setError('');
    setNeedsReauth(false);
    try {
      const res = await api.get('/ads/gsc/sites/');
      const list = res.data.sites || [];
      setSites(list);
      if (res.data.status === 'error') {
        // Token Search Console wygasł albo dostęp odwołano: pokaż „Połącz ponownie".
        setNeedsReauth(res.data.code === 'reauth');
        setError(res.data.code === 'reauth'
          ? t('settings.platforms.gsc.errorExpired')
          : (res.data.message || t('settings.platforms.gsc.errorLoadFailed')));
      } else if (list.length === 0) setError(t('settings.platforms.gsc.noSites'));
    } catch {
      setError(t('settings.platforms.gsc.errorLoadFailed'));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isConnected) loadSites();
  }, [isConnected]);

  useEffect(() => { setSelectedSite(org?.gsc_site_url || ''); }, [org?.gsc_site_url]);

  const handleOAuth = async () => {
    setConnecting(true);
    setError('');
    try {
      // withCredentials: odpowiedź ustawia ciasteczko z nonce (wiąże zgodę z tą przeglądarką).
      const res = await api.get('/accounts/google/gsc/auth/', { withCredentials: true });
      window.location.href = res.data.auth_url;
    } catch {
      setError(t('settings.platforms.gsc.errorConnectFailed'));
      setConnecting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/ads/gsc/connect/', { site_url: selectedSite });
      showSaved();
      onChange && onChange();
    } catch (e) {
      setError(e.response?.data?.message || t('settings.platforms.gsc.errorSaveFailed'));
    } finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      await api.post('/ads/gsc/connect/', { site_url: '' });
      setSites([]);
      setSelectedSite('');
      showSaved();
      onChange && onChange();
    } catch {} finally { setSaving(false); }
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Text size="sm" fw={600} mb="sm">{t('settings.platforms.gsc.title')}</Text>
      <Stack gap="sm">
        <Text size="xs" c="dimmed">{t('settings.platforms.gsc.intro')}</Text>
        {error && (
          <Alert color="red" variant="light" radius="md">
            <Text size="xs">{error}</Text>
          </Alert>
        )}
        {isConnected ? (
          <>
            <Group gap="xs">
              <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: org?.gsc_site_url ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-yellow-5)' }} />
              <Text size="sm" fw={500}>
                {org?.gsc_site_url ? t('settings.platforms.gsc.connectedSite') : t('settings.platforms.gsc.chooseSite')}
              </Text>
              {org?.gsc_site_url && <Text span size="xs" ff="monospace">{org.gsc_site_url}</Text>}
            </Group>
            <Select
              label={t('settings.platforms.gsc.selectLabel')}
              placeholder={loading ? t('settings.platforms.gsc.loading') : t('settings.platforms.gsc.selectPlaceholder')}
              nothingFoundMessage={t('settings.platforms.gsc.noSites')}
              data={sites}
              value={selectedSite || null}
              onChange={(v) => setSelectedSite(v || '')}
              disabled={loading || saving}
              searchable
              size="sm"
              styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
            />
            <Group gap="xs">
              {needsReauth && (
                <Button color="brand" size="xs" onClick={handleOAuth} loading={connecting}>
                  {t('settings.platforms.gsc.reconnect')}
                </Button>
              )}
              <Button color="brand" size="xs" onClick={handleSave} loading={saving}
                disabled={!selectedSite || selectedSite === org?.gsc_site_url}>
                {t('settings.platforms.gsc.save')}
              </Button>
              <Button variant="outline" color="red" size="xs" onClick={handleDisconnect} loading={saving}>
                {t('settings.platforms.gsc.disconnect')}
              </Button>
            </Group>
          </>
        ) : (
          <Button variant="light" color="brand" size="xs" onClick={handleOAuth} loading={connecting} style={{ alignSelf: 'flex-start' }}>
            {t('settings.platforms.gsc.connect')}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // Map legacy 'google' tab to 'platforms'
  const initialTab = tabParam === 'google' ? 'platforms' : (TABS.includes(tabParam) ? tabParam : 'profile');
  const [activeTab, setActiveTabRaw] = useState(initialTab);
  const platformParam = searchParams.get('platform');
  const [selectedPlatform, setSelectedPlatformRaw] = useState(platformParam || 'google_ads');

  const setSelectedPlatform = (p) => {
    setSelectedPlatformRaw(p);
    setSearchParams({ tab: 'platforms', platform: p }, { replace: true });
  };

  const setActiveTab = (tab) => {
    setActiveTabRaw(tab);
    const params = { tab };
    if (tab === 'platforms') params.platform = selectedPlatform;
    setSearchParams(params, { replace: true });
  };

  // Handle Google Search Console OAuth return (?gsc=connected|error)
  useEffect(() => {
    const gsc = searchParams.get('gsc');
    if (!gsc) return;
    setActiveTabRaw('platforms');
    setSelectedPlatformRaw('google_ads');
    if (gsc === 'connected') {
      showToast(t('settings.platforms.gsc.connectedToast'));
      refreshUser();
    } else if (gsc === 'error') {
      showToast(searchParams.get('message') || t('settings.platforms.gsc.errorConnectFailed'), 'error');
    }
    setSearchParams({ tab: 'platforms', platform: 'google_ads' }, { replace: true });
  }, []);

  // Handle Meta OAuth return
  useEffect(() => {
    if (searchParams.get('meta_ads') === 'connected') {
      setActiveTabRaw('platforms');
      setSelectedPlatformRaw('meta_ads');
      refreshUser();
    }
  }, []);
  const [saving, setSaving] = useState(false);

  // Profile
  const [companyName, setCompanyName] = useState(user?.company_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [layout, setLayout] = useState(user?.pref_layout || 'normal');
  const [uiTheme, setUiTheme] = useState(user?.pref_theme || 'light');

  // Google Ads
  const [customerId, setCustomerId] = useState(user?.google_ads_customer_id || '');
  const [oauthLoading, setOauthLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectPlatform, setDisconnectPlatform] = useState('google_ads'); // which platform to disconnect
  const [showChangeAccount, setShowChangeAccount] = useState(false);
  const [accountsList, setAccountsList] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [selectingAccount, setSelectingAccount] = useState(null);
  const isGoogleConnected = !!user?.google_ads_connected || !!user?.google_ads_customer_id;

  // Subscription
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [changePlanLoading, setChangePlanLoading] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);

  // Agent preferences (from backend user model)
  const [autoApply, setAutoApply] = useState(user?.pref_auto_apply ?? false);
  const [autoApplyHighRisk, setAutoApplyHighRisk] = useState(user?.pref_auto_apply_high_risk ?? false);
  const [emailNotifications, setEmailNotifications] = useState(user?.pref_email_alerts ?? true);
  const [emailAlertsWarning, setEmailAlertsWarning] = useState(user?.organization?.pref_email_alerts_warning ?? true);
  const [emailAlertsCritical, setEmailAlertsCritical] = useState(user?.organization?.pref_email_alerts_critical ?? true);
  const [weeklyReport, setWeeklyReport] = useState(user?.pref_weekly_report ?? true);
  const [dailyReport, setDailyReport] = useState(user?.pref_daily_report ?? true);

  // Team
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [orgSaving, setOrgSaving] = useState(false);

  // Account deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const loadMembers = async () => {
    setMembersLoading(true);
    try {
      const [membersRes, orgRes] = await Promise.all([
        api.get('/accounts/organization/members/'),
        api.get('/accounts/organization/'),
      ]);
      setMembers(membersRes.data);
      setOrgName(orgRes.data.name || '');
    } catch {
      // silently handle
    } finally {
      setMembersLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      await api.post('/accounts/organization/invite/', {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
      });
      setInviteSuccess(t('settings.team.invitedToast', { email: inviteEmail }));
      setInviteEmail('');
      loadMembers();
    } catch (err) {
      setInviteError(err.response?.data?.error || t('settings.team.inviteError'));
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    setRemovingId(userId);
    try {
      await api.post('/accounts/organization/remove-member/', { user_id: userId });
      loadMembers();
    } catch {
      // silently handle
    } finally {
      setRemovingId(null);
    }
  };

  const handleSaveOrgName = async () => {
    setOrgSaving(true);
    try {
      await api.patch('/accounts/organization/', { name: orgName });
      showSaved();
    } catch {
      // silently handle
    } finally {
      setOrgSaving(false);
    }
  };

  // Fetch subscription data when tab is active
  useEffect(() => {
    if (activeTab === 'subscription') {
      setSubscriptionLoading(true);
      api.get('/ads/subscription-info/')
        .then(res => setSubscriptionData(res.data))
        .catch(() => setSubscriptionData(null))
        .finally(() => setSubscriptionLoading(false));
      api.get('/ads/plan-status/')
        .then(res => setPlanStatus(res.data))
        .catch(() => setPlanStatus(null));
    }
    if (activeTab === 'team') {
      loadMembers();
    }
  }, [activeTab]);

  const showSaved = () => {
    showToast(t('settings.common.savedToast'));
  };

  const handleGoogleOAuth = async () => {
    setOauthLoading(true);
    try {
      const res = await api.get('/accounts/google/auth/');
      window.location.href = res.data.auth_url;
    } catch {
      // silently handle
    } finally {
      setOauthLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      if (disconnectPlatform === 'google_ads') {
        await api.post('/accounts/disconnect-google-ads/');
        setCustomerId('');
      } else {
        await api.post('/accounts/platform-accounts/disconnect/', { platform: disconnectPlatform });
      }
      await refreshUser();
      setShowDisconnectModal(false);
      showSaved();
    } catch {
      // silently handle
    } finally {
      setDisconnecting(false);
    }
  };

  const handleOpenChangeAccount = async () => {
    setShowChangeAccount(true);
    setAccountsLoading(true);
    setAccountsError('');
    try {
      const res = await getGoogleAccounts();
      setAccountsList(res.data.accounts || []);
    } catch {
      setAccountsError(t('settings.platforms.google.loadAccountsError'));
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleSelectAccount = async (account) => {
    setSelectingAccount(account.id);
    try {
      await api.post('/accounts/connect-google-ads/', {
        customer_id: account.id,
        refresh_token: 'keep',
      });
      setCustomerId(account.id);
      await refreshUser();
      setShowChangeAccount(false);
      showSaved();
    } catch {
      setAccountsError(t('settings.platforms.google.selectAccountError'));
    } finally {
      setSelectingAccount(null);
    }
  };

  const handleSaveGoogle = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      await api.post('/accounts/connect-google-ads/', { customer_id: customerId, refresh_token: 'keep' });
      showSaved();
    } catch {
      // silently handle
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/accounts/profile/', { company_name: companyName, email, pref_layout: layout, pref_theme: uiTheme });
      await refreshUser();
      showSaved();
    } catch {
      // silently handle
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const prefs = {
        pref_auto_apply: autoApply,
        pref_auto_apply_high_risk: autoApplyHighRisk,
        pref_email_alerts: emailNotifications,
        pref_email_alerts_warning: emailAlertsWarning,
        pref_email_alerts_critical: emailAlertsCritical,
        pref_weekly_report: weeklyReport,
        pref_daily_report: dailyReport,
      };
      await Promise.all([
        api.patch('/accounts/profile/', prefs),
        api.patch('/accounts/organization/', prefs),
      ]);
      await refreshUser();
      showSaved();
    } catch {
      // silently handle
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await api.post('/accounts/delete-account/', { password: deletePassword, reason: deleteReason });
      showToast(t('settings.deleteAccount.scheduledToast'), 'info');
      setShowDeleteModal(false);
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err.response?.data?.error || t('settings.deleteAccount.genericError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    setBillingPortalLoading(true);
    try {
      const res = await api.post('/ads/billing-portal/');
      window.location.href = res.data.url;
    } catch {
      // silently handle
    } finally {
      setBillingPortalLoading(false);
    }
  };

  const handleChangePlan = async (planKey) => {
    setChangePlanLoading(planKey);
    try {
      const res = await api.post('/ads/change-plan/', { plan: planKey, ...(planStatus?.currency ? { currency: planStatus.currency } : {}) });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      showToast(res.data.message || t('settings.subscription.planChangedToast'));
      const [ps, si] = await Promise.all([
        api.get('/ads/plan-status/'),
        api.get('/ads/subscription-info/'),
      ]);
      setPlanStatus(ps.data);
      setSubscriptionData(si.data);
    } catch (err) {
      showToast(err.response?.data?.error || t('settings.subscription.planChangeError'), 'error');
    } finally {
      setChangePlanLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Box px="lg" py="md">
      <SEO title={t('settings.seoTitle')} path="/app/settings" noIndex />
      {/* Header */}
      <Group justify="space-between" mb="sm">
        <Title order={3}>{t('settings.title')}</Title>
      </Group>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab} color="brand" mb="lg">
        <Tabs.List>
          <Tabs.Tab value="profile">{t('settings.tabs.profile')}</Tabs.Tab>
          <Tabs.Tab value="platforms">{t('settings.tabs.platforms')}</Tabs.Tab>
          <Tabs.Tab value="subscription">{t('settings.tabs.subscription')}</Tabs.Tab>
          <Tabs.Tab value="agent">{t('settings.tabs.agent')}</Tabs.Tab>
          <Tabs.Tab value="knowledge">{t('settings.tabs.knowledge')}</Tabs.Tab>
          <Tabs.Tab value="team">{t('settings.tabs.team')}</Tabs.Tab>
          {user?.organization?.is_agency && <Tabs.Tab value="agency">{t('settings.tabs.agency')}</Tabs.Tab>}
          <Tabs.Tab value="archive">{t('settings.tabs.archive')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <form onSubmit={handleSaveProfile}>
          <Stack gap="sm" maw={448}>
            <TextInput
              label={t('settings.profile.companyName')}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              size="sm"
              styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
            />
            <TextInput
              label={t('settings.profile.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="sm"
              styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
            />
            <Select
              label={t('settings.profile.layoutLabel')}
              description={
                layout === 'wide'
                  ? t('settings.profile.layoutWideDesc')
                  : layout === 'medium'
                    ? t('settings.profile.layoutMediumDesc')
                    : t('settings.profile.layoutNormalDesc')
              }
              value={layout}
              onChange={setLayout}
              data={[
                { value: 'normal', label: t('settings.profile.layoutNormal') },
                { value: 'medium', label: t('settings.profile.layoutMedium') },
                { value: 'wide', label: t('settings.profile.layoutWide') },
              ]}
              size="sm"
              styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
            />
            <Select
              label={t('settings.profile.themeLabel')}
              description={uiTheme === 'blue' ? t('settings.profile.themeBlueDesc') : t('settings.profile.themeLightDesc')}
              value={uiTheme}
              onChange={setUiTheme}
              data={[
                { value: 'light', label: t('settings.profile.themeLight') },
                { value: 'blue', label: t('settings.profile.themeBlue') },
              ]}
              size="sm"
              styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
            />
            <Box>
              <Button
                type="submit"
                color="brand"
                size="xs"
                loading={saving}
              >
                {saving ? t('settings.common.saving') : t('settings.common.save')}
              </Button>
            </Box>
          </Stack>
        </form>
      )}

      {/* Claude Code / MCP integration - below profile tab */}
      {activeTab === 'profile' && <McpIntegration />}

      {/* Delete Account section - below profile tab */}
      {activeTab === 'profile' && (
        <Paper withBorder p="md" mt="xl" maw={448} style={{ borderColor: 'var(--mantine-color-red-3)' }}>
          <Text size="sm" fw={600} c="red.7" mb={4}>{t('settings.deleteAccount.sectionTitle')}</Text>
          <Text size="xs" c="dimmed" mb="sm">
            {t('settings.deleteAccount.sectionDesc')}
          </Text>
          <Button
            color="red"
            variant="light"
            size="xs"
            onClick={() => { setShowDeleteModal(true); setDeletePassword(''); setDeleteReason(''); setDeleteError(''); }}
          >
            {t('settings.deleteAccount.button')}
          </Button>
        </Paper>
      )}

      {/* Delete Account Modal */}
      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('settings.deleteAccount.modalTitle')}
        size="sm"
        centered
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('settings.deleteAccount.modalDesc')}
          </Text>
          <TextInput
            label={t('settings.deleteAccount.passwordLabel')}
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            size="sm"
            error={deleteError}
          />
          <Textarea
            label={t('settings.deleteAccount.reasonLabel')}
            placeholder={t('settings.deleteAccount.reasonPlaceholder')}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            size="sm"
            minRows={2}
            autosize
            maxRows={5}
            description={t('settings.deleteAccount.reasonDesc')}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setShowDeleteModal(false)}>{t('settings.common.cancel')}</Button>
            <Button
              color="red"
              size="xs"
              loading={deleteLoading}
              onClick={handleDeleteAccount}
              disabled={!deletePassword}
            >
              {t('settings.deleteAccount.button')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Platforms Tab */}
      {activeTab === 'platforms' && (
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left: platform list */}
          <div className="md:w-[220px] shrink-0">
            <Stack gap={6}>
              {PLATFORMS.map(p => {
                const isConnected = p.id === 'google_ads' && !!user?.google_ads_customer_id;
                const isSelected = selectedPlatform === p.id;
                return (
                  <Paper
                    key={p.id}
                    withBorder
                    p="sm"
                    radius="md"
                    onClick={() => p.available && setSelectedPlatform(p.id)}
                    style={{
                      cursor: p.available ? 'pointer' : 'default',
                      opacity: p.available ? 1 : 0.5,
                      borderColor: isSelected ? 'var(--mantine-color-brand-4)' : undefined,
                      backgroundColor: isSelected ? 'var(--mantine-color-brand-0)' : undefined,
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <div className={`w-8 h-8 rounded-md ${p.bgClass} flex items-center justify-center shrink-0`}>
                        <Text size="sm" fw={700} c={p.color}>{p.icon}</Text>
                      </div>
                      <div className="min-w-0 flex-1">
                        <Text size="xs" fw={600} truncate="end">{p.label}</Text>
                        {isConnected && <Text size="xs" c="green.6" lh={1.2}>{t('settings.platforms.connected')}</Text>}
                        {!p.available && <Text size="xs" c="dimmed" lh={1.2}>{t('settings.platforms.comingSoonShort')}</Text>}
                      </div>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          </div>

          {/* Right: selected platform settings */}
          <div className="flex-1 min-w-0">
            {selectedPlatform === 'google_ads' && (
              <Stack gap="sm" maw={448}>
                {isGoogleConnected ? (
                  <>
                    <Paper withBorder p="md" radius="md" style={{ borderColor: 'var(--mantine-color-green-3)', backgroundColor: 'var(--mantine-color-green-0)' }}>
                      <Group gap="xs" mb="xs">
                        <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-green-5)' }} />
                        <Text size="sm" fw={500}>{t('settings.platforms.google.connectedTitle')}</Text>
                      </Group>
                      {user?.google_ads_customer_id && (
                        <Text size="xs" c="dimmed">
                          {t('settings.platforms.google.customerIdLabel')} <Text span ff="monospace" c="dark">{user.google_ads_customer_id}</Text>
                        </Text>
                      )}
                      <Group gap="xs" mt="xs">
                        <Button variant="outline" color="brand" size="xs" onClick={handleOpenChangeAccount}>
                          {t('settings.platforms.google.changeAccount')}
                        </Button>
                        <Button variant="outline" color="gray" size="xs" loading={oauthLoading} onClick={async () => {
                          setOauthLoading(true);
                          try {
                            const res = await api.get('/accounts/google/auth/?add=1');
                            window.location.href = res.data.auth_url;
                          } catch { setOauthLoading(false); }
                        }}>
                          {t('settings.platforms.google.addAccount')}
                        </Button>
                      </Group>
                    </Paper>

                    {showChangeAccount && (
                      <Paper withBorder p="md" radius="md">
                        <Text size="sm" fw={500} mb={4}>{t('settings.platforms.google.chooseAccountTitle')}</Text>
                        <Text size="xs" c="dimmed" mb="sm">{t('settings.platforms.google.chooseAccountDesc')}</Text>

                        {accountsLoading && (
                          <Flex align="center" justify="center" gap="xs" py="md">
                            <Loader size="xs" color="brand" />
                            <Text size="sm" c="dimmed">{t('settings.platforms.google.loadingAccounts')}</Text>
                          </Flex>
                        )}

                        {accountsError && !accountsLoading && (
                          <Alert color="red" variant="light" icon={<WarningCircle size={16} />} mb="sm">
                            <Text size="sm">{accountsError}</Text>
                          </Alert>
                        )}

                        {!accountsLoading && !accountsError && accountsList.length === 0 && (
                          <Text size="xs" c="dimmed" py="xs">{t('settings.platforms.google.noAccounts')}</Text>
                        )}

                        {!accountsLoading && accountsList.length > 0 && (
                          <Stack gap={6} mah={192} style={{ overflowY: 'auto' }}>
                            {accountsList.map(account => {
                              const isCurrent = account.id === user?.google_ads_customer_id;
                              return (
                                <Paper
                                  key={account.id} withBorder p="xs" radius="md"
                                  onClick={() => handleSelectAccount(account)}
                                  style={{
                                    cursor: selectingAccount ? 'default' : 'pointer',
                                    opacity: selectingAccount ? 0.5 : 1,
                                    borderColor: isCurrent ? 'var(--mantine-color-brand-3)' : undefined,
                                    backgroundColor: isCurrent ? 'var(--mantine-color-brand-0)' : undefined,
                                  }}
                                >
                                  <Group justify="space-between" wrap="nowrap">
                                    <Box style={{ minWidth: 0, flex: 1 }}>
                                      <Group gap="xs">
                                        <Text size="sm" fw={500} truncate="end">{account.name || t('settings.common.unnamedAccount')}</Text>
                                        {isCurrent && <CheckCircle size={14} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />}
                                      </Group>
                                      <Group gap="xs" mt={2}>
                                        <Text size="xs" ff="monospace" c="dimmed">{account.id}</Text>
                                        <Text size="xs" c="gray.5">{account.is_test ? t('settings.platforms.google.accountTest') : t('settings.platforms.google.accountRegular')}</Text>
                                      </Group>
                                    </Box>
                                    {selectingAccount === account.id && <Loader size="xs" color="brand" style={{ flexShrink: 0 }} />}
                                  </Group>
                                </Paper>
                              );
                            })}
                          </Stack>
                        )}

                        <Button variant="subtle" color="gray" size="xs" mt="sm" onClick={() => setShowChangeAccount(false)}>
                          {t('settings.common.cancel')}
                        </Button>
                      </Paper>
                    )}

                    <Paper withBorder p="md" radius="md">
                      <Text size="sm" fw={500} mb={4}>{t('settings.platforms.google.disconnectTitle')}</Text>
                      <Text size="xs" c="dimmed" mb="sm">
                        {t('settings.platforms.google.disconnectDesc')}
                      </Text>
                      <Button variant="outline" color="red" size="xs" onClick={() => { setDisconnectPlatform('google_ads'); setShowDisconnectModal(true); }}>
                        {t('settings.platforms.google.disconnectButton')}
                      </Button>
                    </Paper>

                    {/* GA4 Integration */}
                    <GA4Settings org={user?.organization} showSaved={showSaved} />

                    {/* Google Search Console */}
                    {SHOW_GSC && <GSCSettings org={user?.organization} showSaved={showSaved} onChange={refreshUser} />}
                  </>
                ) : (
                  <Paper withBorder p="xl" radius="md" ta="center">
                    <Text size="sm" fw={500} mb={4}>{t('settings.platforms.google.notConnectedTitle')}</Text>
                    <Text size="xs" c="dimmed" mb="md">
                      {t('settings.platforms.google.notConnectedDesc')}
                    </Text>
                    <Button color="brand" size="sm" onClick={handleGoogleOAuth} loading={oauthLoading}>
                      {oauthLoading ? t('settings.platforms.google.connecting') : t('settings.platforms.google.connect')}
                    </Button>
                  </Paper>
                )}
              </Stack>
            )}

            {selectedPlatform === 'meta_ads' && (
              <Stack gap="sm" maw={448}>
                {user?.organization?.platform_accounts?.some(pa => pa.platform === 'meta_ads' && pa.is_active) ? (
                  <>
                    <Paper withBorder p="md" radius="md" style={{ borderColor: 'var(--mantine-color-green-3)', backgroundColor: 'var(--mantine-color-green-0)' }}>
                      <Group gap="xs" mb="xs">
                        <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-green-5)' }} />
                        <Text size="sm" fw={500}>{t('settings.platforms.meta.connectedTitle')}</Text>
                      </Group>
                      {user.organization.platform_accounts
                        .filter(pa => pa.platform === 'meta_ads' && pa.is_active)
                        .map(pa => (
                          <Text key={pa.id} size="xs" c="dimmed">
                            {pa.account_name}: <Text span ff="monospace" c="dark">{pa.account_id}</Text>
                          </Text>
                        ))
                      }
                      <Group gap="xs" mt="xs">
                        <Button variant="outline" color="brand" size="xs" loading={oauthLoading} onClick={async () => {
                          setOauthLoading(true);
                          try {
                            const res = await api.get('/accounts/meta/auth/');
                            window.location.href = res.data.auth_url;
                          } catch { setOauthLoading(false); }
                        }}>
                          {t('settings.platforms.meta.refreshToken')}
                        </Button>
                        <MetaAccountSwitcher onSwitch={() => refreshUser()} />
                      </Group>
                    </Paper>
                    <MetaPixelPanel />
                    <Paper withBorder p="md" radius="md">
                      <Text size="sm" fw={500} mb={4}>{t('settings.platforms.meta.disconnectTitle')}</Text>
                      <Text size="xs" c="dimmed" mb="sm">
                        {t('settings.platforms.meta.disconnectDesc')}
                      </Text>
                      <Button variant="outline" color="red" size="xs" onClick={() => { setDisconnectPlatform('meta_ads'); setShowDisconnectModal(true); }}>
                        {t('settings.platforms.meta.disconnectButton')}
                      </Button>
                    </Paper>
                  </>
                ) : (
                  <Paper withBorder p="xl" radius="md" ta="center">
                    <Text size="sm" fw={500} mb={4}>{t('settings.platforms.meta.notConnectedTitle')}</Text>
                    <Text size="xs" c="dimmed" mb="md">
                      {t('settings.platforms.meta.notConnectedDesc')}
                    </Text>
                    <Button color="brand" size="sm" loading={oauthLoading} onClick={async () => {
                      setOauthLoading(true);
                      try {
                        const res = await api.get('/accounts/meta/auth/');
                        window.location.href = res.data.auth_url;
                      } catch { setOauthLoading(false); }
                    }}>
                      {oauthLoading ? t('settings.platforms.meta.connecting') : t('settings.platforms.meta.connect')}
                    </Button>
                  </Paper>
                )}
              </Stack>
            )}

            {selectedPlatform !== 'google_ads' && selectedPlatform !== 'meta_ads' && (() => {
              const platformData = PLATFORMS.find(p => p.id === selectedPlatform);
              return !platformData?.available ? (
                <Paper withBorder p="xl" radius="md" ta="center">
                  <Text size="lg" fw={700} mb="xs">{platformData?.label}</Text>
                  <Badge color="gray" variant="light" size="sm" mb="md">{t('settings.platforms.comingSoonTitle')}</Badge>
                  <Text size="sm" c="dimmed">
                    {t('settings.platforms.comingSoonDesc')}
                  </Text>
                </Paper>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <Stack gap="sm" maw={512}>
          {subscriptionLoading ? (
            <Flex align="center" justify="center" gap="xs" py="xl">
              <Loader size="xs" color="brand" />
              <Text size="sm" c="dimmed">{t('settings.subscription.loading')}</Text>
            </Flex>
          ) : user?.organization?.is_agency ? (
            /* Agency mode - redirect to agency page */
            <Paper withBorder p="xl" radius="md" ta="center">
              <Text size="sm" fw={500} mb={4}>{t('settings.subscription.agencyPlanTitle')}</Text>
              <Text size="xs" c="dimmed" mb="md">
                {t('settings.subscription.agencyDesc')}
              </Text>
              <Button
                component={Link}
                to="/app/agency"
                color="brand"
                size="sm"
              >
                {t('settings.subscription.agencyGoTo')}
              </Button>
            </Paper>
          ) : subscriptionData?.subscription_active ? (
            <>
              {/* Pakiet (model pakietowy) */}
              <Paper withBorder p="md" radius="md">
                <Group justify="space-between" mb="sm">
                  <Text size="sm" fw={500}>{t('settings.subscription.planTitle', { plan: planStatus?.plan_label || '...' })}</Text>
                  <Badge color="green" variant="light" size="sm">{t('settings.subscription.active')}</Badge>
                </Group>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Box>
                    <Text size="xs" c="dimmed">{t('settings.subscription.campaigns')}</Text>
                    <Text size="sm" fw={600}>
                      {planStatus ? `${planStatus.managed_campaigns} / ${planStatus.campaign_limit ?? '∞'}` : '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">{t('settings.subscription.adAccounts')}</Text>
                    <Text size="sm" fw={600}>
                      {planStatus ? `${planStatus.managed_accounts} / ${planStatus.account_limit ?? '∞'}` : '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">{t('settings.subscription.monthlyCost')}</Text>
                    <Text size="sm" fw={600}>
                      {(() => {
                        const billCur = planStatus?.currency || 'PLN';
                        const cur = planStatus?.plans?.find(p => p.key === planStatus.plan);
                        const priceCur = cur?.prices?.[billCur] ?? planStatus?.plan_price;
                        if (priceCur != null) return t('settings.subscription.pricePerMonthNet', { price: formatMoney(priceCur, billCur) });
                        if (subscriptionData.total_monthly_cost) return t('settings.subscription.priceNet', { price: formatMoney(subscriptionData.total_monthly_cost, billCur) });
                        return '-';
                      })()}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">{t('settings.subscription.nextPayment')}</Text>
                    <Text size="sm" fw={600}>
                      {subscriptionData.next_billing_date ? formatDate(subscriptionData.next_billing_date) : '-'}
                    </Text>
                  </Box>
                </div>
              </Paper>

              {/* Zmiana pakietu */}
              {planStatus?.plans?.length > 0 && (
                <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                  <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                    <Text size="sm" fw={500}>{t('settings.subscription.changePlan')}</Text>
                    <Text size="xs" c="dimmed">{t('settings.subscription.changePlanDesc')}</Text>
                  </Box>
                  <Stack gap={0}>
                    {planStatus.plans.map((p, i) => {
                      const isCurrent = p.key === planStatus.plan;
                      const isUpgrade = p.price > (planStatus.plan_price || 0);
                      return (
                        <Group key={p.key} justify="space-between" px="md" py="sm"
                          style={{ borderTop: i === 0 ? 'none' : '1px solid var(--mantine-color-gray-1)' }}>
                          <Box>
                            <Group gap="xs">
                              <Text size="sm" fw={600}>{p.label}</Text>
                              {isCurrent && <Badge size="xs" variant="light" color="brand">{t('settings.subscription.current')}</Badge>}
                            </Group>
                            <Text size="xs" c="dimmed">
                              {p.budget_max_daily
                                ? t('settings.subscription.planBudgetLine', { budget: p.budget_max_daily })
                                : t('settings.subscription.planLimitsLine', { campaigns: p.campaigns, accounts: p.accounts })}
                            </Text>
                          </Box>
                          <Group gap="sm" wrap="nowrap">
                            <Text size="sm" fw={700}>{formatMoney(p.prices?.[planStatus.currency || 'PLN'] ?? p.price, planStatus.currency || 'PLN')}<Text span size="xs" c="dimmed">{t('settings.subscription.perMonthShort')}</Text></Text>
                            {!isCurrent && (
                              <Button size="xs" variant={isUpgrade ? 'filled' : 'outline'} color="brand"
                                loading={changePlanLoading === p.key}
                                onClick={() => handleChangePlan(p.key)}>
                                {isUpgrade ? t('settings.subscription.upgrade') : t('settings.subscription.change')}
                              </Button>
                            )}
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                </Paper>
              )}

              {/* Campaigns table */}
              {subscriptionData.campaigns && subscriptionData.campaigns.length > 0 && (
                <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                  <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                    <Text size="sm" fw={500}>{t('settings.subscription.managedCampaigns')}</Text>
                  </Box>
                  <div style={{ overflowX: 'auto' }}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('settings.subscription.campaignColumn')}</Table.Th>
                        <Table.Th style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('settings.subscription.statusColumn')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {subscriptionData.campaigns.map(c => (
                        <Table.Tr key={c.id}>
                          <Table.Td><Text size="sm">{c.name}</Text></Table.Td>
                          <Table.Td>
                            {c.billing_status === 'subscription' && (
                              <Badge color="green" variant="light" size="sm">{t('settings.subscription.statusSubscription')}</Badge>
                            )}
                            {c.billing_status === 'trial' && (
                              <Badge color="blue" variant="light" size="sm">
                                {t('settings.subscription.statusTrial', { days: c.trial_days_remaining })}
                              </Badge>
                            )}
                            {c.billing_status === 'inactive' && (
                              <Badge color="gray" variant="light" size="sm">{t('settings.subscription.statusInactive')}</Badge>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  </div>
                </Paper>
              )}

              {/* Billing portal button */}
              <Box>
                <Button
                  variant="outline"
                  color="brand"
                  size="xs"
                  onClick={handleOpenBillingPortal}
                  loading={billingPortalLoading}
                >
                  {billingPortalLoading ? t('settings.subscription.opening') : t('settings.subscription.manageInStripe')}
                </Button>
              </Box>
            </>
          ) : (
            /* No active subscription */
            <Paper withBorder p="xl" radius="md" ta="center">
              <Text size="sm" fw={500} mb={4}>{t('settings.subscription.noSubscriptionTitle')}</Text>
              <Text size="xs" c="dimmed" mb="md">
                {t('settings.subscription.noSubscriptionDesc')}
              </Text>

              {/* Show trial campaigns if any */}
              {subscriptionData?.trial_campaigns && subscriptionData.trial_campaigns.length > 0 && (
                <Box mb="md" ta="left">
                  <Text size="xs" c="dimmed" mb="xs">{t('settings.subscription.trialCampaignsLabel')}</Text>
                  {subscriptionData.trial_campaigns.map(tc => (
                    <Group key={tc.id} justify="space-between" py={6} style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }}>
                      <Text size="sm">{tc.name}</Text>
                      <Badge color="blue" variant="light" size="sm">
                        {t('settings.subscription.daysRemaining', { days: tc.days_remaining })}
                      </Badge>
                    </Group>
                  ))}
                </Box>
              )}

              <Button
                component={Link}
                to="/app/campaigns"
                color="brand"
                size="sm"
              >
                {t('settings.subscription.goToCampaigns')}
              </Button>
            </Paper>
          )}

          {/* Invoices section */}
          {subscriptionData?.invoices && subscriptionData.invoices.length > 0 && (
            <Box mt="lg">
              <Text size="xs" tt="uppercase" lts="0.05em" c="dimmed" mb="sm">{t('settings.subscription.invoices')}</Text>
              <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ fontSize: 11 }}>{t('settings.subscription.invoiceNumber')}</Table.Th>
                      <Table.Th style={{ fontSize: 11 }}>{t('settings.subscription.invoiceDate')}</Table.Th>
                      <Table.Th style={{ fontSize: 11, textAlign: 'right' }}>{t('settings.subscription.invoiceAmount')}</Table.Th>
                      <Table.Th style={{ fontSize: 11, textAlign: 'center' }}>{t('settings.subscription.invoiceStatus')}</Table.Th>
                      <Table.Th style={{ fontSize: 11, textAlign: 'right' }}>{t('settings.subscription.invoicePdf')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {subscriptionData.invoices.map(inv => (
                      <Table.Tr key={inv.id}>
                        <Table.Td><Text size="sm">{inv.number || inv.id.slice(-8)}</Text></Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {new Date(inv.date * 1000).toLocaleDateString('pl-PL')}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" fw={500}>
                            {inv.amount.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} {inv.currency}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          {inv.status === 'paid' && (
                            <Badge color="green" variant="light" size="xs">{t('settings.subscription.invoicePaid')}</Badge>
                          )}
                          {inv.status === 'open' && (
                            <Badge color="yellow" variant="light" size="xs">{t('settings.subscription.invoiceOpen')}</Badge>
                          )}
                          {inv.status !== 'paid' && inv.status !== 'open' && (
                            <Badge color="gray" variant="light" size="xs">{inv.status}</Badge>
                          )}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {inv.pdf_url && (
                            <Text
                              component="a"
                              href={inv.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="xs"
                              c="brand"
                              fw={500}
                              style={{ textDecoration: 'none' }}
                            >
                              {t('settings.subscription.invoiceDownload')}
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                </div>
              </Paper>
            </Box>
          )}
        </Stack>
      )}

      {/* Agent Tab */}
      {activeTab === 'agent' && (
        <form onSubmit={handleSavePreferences}>
          <Stack gap={0} maw={448}>
            <Group justify="space-between" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Box>
                <Text size="sm">{t('settings.agent.autoLowRiskTitle')}</Text>
                <Text size="xs" c="dimmed">{t('settings.agent.autoLowRiskDesc')}</Text>
              </Box>
              <Switch
                checked={autoApply}
                onChange={() => setAutoApply(!autoApply)}
                color="brand"
                styles={{ track: { cursor: 'pointer' } }}
              />
            </Group>
            <Group justify="space-between" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Box>
                <Text size="sm">{t('settings.agent.autoAllTitle')}</Text>
                <Text size="xs" c="dimmed">{t('settings.agent.autoAllDesc')}</Text>
              </Box>
              <Switch
                checked={autoApplyHighRisk}
                onChange={() => setAutoApplyHighRisk(!autoApplyHighRisk)}
                color="brand"
                disabled={!autoApply}
                styles={{ track: { cursor: autoApply ? 'pointer' : 'not-allowed' } }}
              />
            </Group>
            <Group justify="space-between" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Box>
                <Text size="sm">{t('settings.agent.emailNotifTitle')}</Text>
                <Text size="xs" c="dimmed">{t('settings.agent.emailNotifDesc')}</Text>
              </Box>
              <Switch
                checked={emailNotifications}
                onChange={() => setEmailNotifications(!emailNotifications)}
                color="brand"
                styles={{ track: { cursor: 'pointer' } }}
              />
            </Group>
            <Group justify="space-between" py="sm" pl="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Box>
                <Text size="sm">{t('settings.agent.criticalTitle')}</Text>
                <Text size="xs" c="dimmed">{t('settings.agent.criticalDesc')}</Text>
              </Box>
              <Switch
                checked={emailAlertsCritical}
                onChange={() => setEmailAlertsCritical(!emailAlertsCritical)}
                color="brand"
                disabled={!emailNotifications}
                styles={{ track: { cursor: emailNotifications ? 'pointer' : 'not-allowed' } }}
              />
            </Group>
            <Group justify="space-between" py="sm" pl="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Box>
                <Text size="sm">{t('settings.agent.warningTitle')}</Text>
                <Text size="xs" c="dimmed">{t('settings.agent.warningDesc')}</Text>
              </Box>
              <Switch
                checked={emailAlertsWarning}
                onChange={() => setEmailAlertsWarning(!emailAlertsWarning)}
                color="brand"
                disabled={!emailNotifications}
                styles={{ track: { cursor: emailNotifications ? 'pointer' : 'not-allowed' } }}
              />
            </Group>
            <Group justify="space-between" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Box>
                <Text size="sm">{t('settings.agent.dailyReportTitle')}</Text>
                <Text size="xs" c="dimmed">{t('settings.agent.dailyReportDesc')}</Text>
              </Box>
              <Switch
                checked={dailyReport}
                onChange={() => setDailyReport(!dailyReport)}
                color="brand"
                styles={{ track: { cursor: 'pointer' } }}
              />
            </Group>
            <Group justify="space-between" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Box>
                <Text size="sm">{t('settings.agent.weeklyReportTitle')}</Text>
                <Text size="xs" c="dimmed">{t('settings.agent.weeklyReportDesc')}</Text>
              </Box>
              <Switch
                checked={weeklyReport}
                onChange={() => setWeeklyReport(!weeklyReport)}
                color="brand"
                styles={{ track: { cursor: 'pointer' } }}
              />
            </Group>
            <Box mt="sm">
              <Button
                type="submit"
                color="brand"
                size="xs"
                loading={saving}
              >
                {saving ? t('settings.agent.saving') : t('settings.agent.savePreferences')}
              </Button>
            </Box>
          </Stack>
        </form>
      )}

      {/* Knowledge Tab */}
      {activeTab === 'knowledge' && <KnowledgePanel />}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <Stack gap="md" maw={560}>
          {/* Org name */}
          <Paper withBorder p="md" radius="md">
            <Text size="sm" fw={600} mb="sm">{t('settings.team.orgTitle')}</Text>
            <Group gap="sm" align="flex-end">
              <TextInput
                label={t('settings.team.orgNameLabel')}
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                size="sm"
                style={{ flex: 1 }}
                styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
              />
              <Button
                color="brand"
                size="sm"
                onClick={handleSaveOrgName}
                loading={orgSaving}
              >
                {t('settings.team.save')}
              </Button>
            </Group>
          </Paper>

          {/* Members list */}
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
              <Text size="sm" fw={600}>{t('settings.team.membersTitle')}</Text>
            </Box>

            {membersLoading ? (
              <Flex align="center" justify="center" gap="xs" py="xl">
                <Loader size="xs" color="brand" />
                <Text size="sm" c="dimmed">{t('settings.team.loading')}</Text>
              </Flex>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('settings.team.emailColumn')}</Table.Th>
                    <Table.Th style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('settings.team.roleColumn')}</Table.Th>
                    <Table.Th style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', width: 48 }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {members.map(m => {
                    const isCurrentUser = m.user_id === user?.id;
                    const roleIcon = m.role === 'owner'
                      ? <Crown size={14} weight="fill" color="var(--mantine-color-yellow-6)" />
                      : m.role === 'admin'
                        ? <ShieldCheck size={14} weight="fill" color="var(--mantine-color-brand-5)" />
                        : <Eye size={14} color="var(--mantine-color-gray-5)" />;
                    const roleLabel = m.role === 'owner' ? t('settings.team.roleOwner') : m.role === 'admin' ? t('settings.team.roleAdmin') : t('settings.team.roleViewer');
                    return (
                      <Table.Tr key={m.id}>
                        <Table.Td>
                          <Group gap="xs">
                            <Text size="sm">{m.email}</Text>
                            {isCurrentUser && <Badge size="xs" variant="light" color="gray">{t('settings.team.you')}</Badge>}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6}>
                            {roleIcon}
                            <Text size="sm" c="dimmed">{roleLabel}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          {!isCurrentUser && m.role !== 'owner' && (
                            <Tooltip label={t('settings.team.removeTooltip')} position="left">
                              <Button
                                variant="subtle"
                                color="red"
                                size="compact-xs"
                                loading={removingId === m.user_id}
                                onClick={() => handleRemoveMember(m.user_id)}
                                style={{ padding: 4 }}
                              >
                                <Trash size={14} />
                              </Button>
                            </Tooltip>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {members.length === 0 && !membersLoading && (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text size="sm" c="dimmed" ta="center" py="sm">{t('settings.team.noMembers')}</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
              </div>
            )}
          </Paper>

          {/* Invite form */}
          <Paper withBorder p="md" radius="md">
            <Group gap="xs" mb="sm">
              <UserPlus size={16} color="var(--mantine-color-brand-5)" />
              <Text size="sm" fw={600}>{t('settings.team.inviteTitle')}</Text>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">
              {t('settings.team.inviteDesc')}
            </Text>

            <form onSubmit={handleInvite}>
              <Group gap="sm" align="flex-end">
                <TextInput
                  label={t('settings.team.inviteEmailLabel')}
                  placeholder={t('settings.team.inviteEmailPlaceholder')}
                  value={inviteEmail}
                  onChange={(e) => { setInviteEmail(e.target.value); setInviteError(''); setInviteSuccess(''); }}
                  size="sm"
                  style={{ flex: 1 }}
                  styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
                />
                <Select
                  label={t('settings.team.inviteRoleLabel')}
                  value={inviteRole}
                  onChange={setInviteRole}
                  data={[
                    { value: 'viewer', label: t('settings.team.roleViewer') },
                    { value: 'admin', label: t('settings.team.roleAdmin') },
                  ]}
                  size="sm"
                  w={160}
                  styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
                />
                <Button
                  type="submit"
                  color="brand"
                  size="sm"
                  loading={inviting}
                  leftSection={<UserPlus size={14} />}
                >
                  {t('settings.team.inviteButton')}
                </Button>
              </Group>
            </form>

            {inviteError && (
              <Alert color="red" variant="light" mt="sm" icon={<WarningCircle size={16} />}>
                <Text size="sm">{inviteError}</Text>
              </Alert>
            )}
            {inviteSuccess && (
              <Alert color="green" variant="light" mt="sm" icon={<CheckCircle size={16} />}>
                <Text size="sm">{inviteSuccess}</Text>
              </Alert>
            )}
          </Paper>

          {/* Role descriptions */}
          <Paper withBorder p="md" radius="md">
            <Text size="sm" fw={600} mb="sm">{t('settings.team.rolesTitle')}</Text>
            <Stack gap={8}>
              <Group gap="xs">
                <Crown size={14} weight="fill" color="var(--mantine-color-yellow-6)" />
                <Text size="xs"><Text span fw={600}>{t('settings.team.roleOwner')}</Text> {t('settings.team.roleOwnerDesc')}</Text>
              </Group>
              <Group gap="xs">
                <ShieldCheck size={14} weight="fill" color="var(--mantine-color-brand-5)" />
                <Text size="xs"><Text span fw={600}>{t('settings.team.roleAdmin')}</Text> {t('settings.team.roleAdminDesc')}</Text>
              </Group>
              <Group gap="xs">
                <Eye size={14} color="var(--mantine-color-gray-5)" />
                <Text size="xs"><Text span fw={600}>{t('settings.team.roleViewer')}</Text> {t('settings.team.roleViewerDesc')}</Text>
              </Group>
            </Stack>
          </Paper>
        </Stack>
      )}


      {/* Disconnect confirmation modal */}
      <Modal
        opened={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        title={<Text size="sm" fw={600}>{t('settings.disconnectModal.title', { platform: disconnectPlatform === 'meta_ads' ? 'Meta Ads' : 'Google Ads' })}</Text>}
        centered
        size="sm"
        overlayProps={{ backgroundOpacity: 0.3 }}
      >
        <Text size="xs" c="dimmed" mb="md">
          {disconnectPlatform === 'meta_ads'
            ? t('settings.platforms.meta.disconnectDesc')
            : t('settings.platforms.google.disconnectDesc')
          }
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button
            variant="outline"
            color="gray"
            size="xs"
            onClick={() => setShowDisconnectModal(false)}
          >
            {t('settings.common.cancel')}
          </Button>
          <Button
            color="red"
            size="xs"
            onClick={handleDisconnect}
            loading={disconnecting}
          >
            {disconnecting ? t('settings.disconnectModal.disconnecting') : t('settings.disconnectModal.confirm')}
          </Button>
        </Group>
      </Modal>

      {/* Agency Tab */}
      {activeTab === 'agency' && user?.organization?.is_agency && (
        <AgencyTab org={user?.organization} showSaved={showSaved} />
      )}

      {/* Archive Tab */}
      {activeTab === 'archive' && (
        <ArchivedCampaigns />
      )}
    </Box>
  );
}

function ArchivedCampaigns() {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    api.get('/ads/my-campaigns/?archived=1')
      .then(res => setCampaigns(res.data.campaigns || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRestore = async (id) => {
    setRestoring(id);
    try {
      await api.post(`/ads/campaigns/${id}/archive/`, { action: 'restore' });
      setCampaigns(prev => prev.filter(c => c.id !== id));
      showToast(t('settings.archive.restoredToast'), 'success');
    } catch {
      showToast(t('settings.archive.restoreError'), 'error');
    }
    setRestoring(null);
  };

  if (loading) return <Flex align="center" justify="center" py="xl"><Loader size="sm" color="brand" /></Flex>;

  return (
    <Stack gap="md" maw={640}>
      <Text size="sm" c="dimmed">
        {t('settings.archive.intro')}
      </Text>
      {campaigns.length === 0 ? (
        <Paper withBorder p="xl" ta="center" radius="md">
          <Text size="sm" c="dimmed">{t('settings.archive.empty')}</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {campaigns.map(c => (
            <Paper key={c.id} withBorder p="sm" radius="md">
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={500} truncate>{c.name}</Text>
                  <Group gap="xs" mt={2}>
                    <Text size="xs" c="dimmed">{c.platform === 'meta_ads' ? 'Meta Ads' : 'Google Ads'}</Text>
                    <Text size="xs" c="dimmed">{t('settings.archive.spend', { amount: parseFloat(c.cost || 0).toFixed(2), cur: currencySymbol(c.currency) })}</Text>
                    <Text size="xs" c="dimmed">{t('settings.archive.clicks', { clicks: c.clicks || 0 })}</Text>
                  </Group>
                </Box>
                <Button size="compact-xs" variant="light" color="brand" loading={restoring === c.id} onClick={() => handleRestore(c.id)}>
                  {t('settings.archive.restore')}
                </Button>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
